/**
 * Reset Password Page - Reset password using token from email
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageTransition from '../components/common/PageTransition';
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckIcon from '@mui/icons-material/CheckCircle';
import { useMutation } from '@tanstack/react-query';
import api from '../services/api';

interface ResetPasswordData {
  token: string;
  new_password: string;
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const resetPasswordMutation = useMutation({
    mutationFn: async (data: ResetPasswordData) => {
      const response = await api.post('/auth/reset-password', data);
      return response.data;
    },
    onSuccess: () => {
      setSuccess(true);
      setError('');
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to reset password');
      setSuccess(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid reset link');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    resetPasswordMutation.mutate({
      token,
      new_password: newPassword,
    });
  };

  return (
    <PageTransition>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Logo/Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '16px',
                bgcolor: success ? 'success.main' : 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              {success ? (
                <CheckIcon sx={{ fontSize: 32, color: 'white' }} />
              ) : (
                <LockResetIcon sx={{ fontSize: 32, color: 'white' }} />
              )}
            </Box>

            {/* Reset Password Card */}
            <Card sx={{ width: '100%', maxWidth: 480 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography
                  component="h1"
                  variant="h1"
                  align="center"
                  sx={{
                    fontSize: '1.75rem',
                    fontWeight: 700,
                    mb: 1,
                    color: 'text.primary',
                  }}
                >
                  {success ? 'Password Reset Successful' : 'Reset Password'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ mb: 4 }}
                >
                  {success
                    ? 'Redirecting to login page...'
                    : 'Enter your new password below'}
                </Typography>

                {success ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Your password has been reset successfully!
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                      You will be redirected to the login page in a few seconds...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {error && (
                      <Alert
                        severity="error"
                        sx={{
                          mb: 3,
                          borderRadius: '8px',
                        }}
                      >
                        {error}
                      </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} noValidate>
                      <Box sx={{ mb: 2.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 0.75,
                            fontWeight: 500,
                            color: 'text.primary',
                            fontSize: '0.875rem',
                          }}
                        >
                          New Password
                        </Typography>
                        <TextField
                          required
                          fullWidth
                          name="newPassword"
                          type={showNewPassword ? 'text' : 'password'}
                          id="newPassword"
                          autoFocus
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          disabled={resetPasswordMutation.isPending || !token}
                          placeholder="Enter new password"
                          InputProps={{
                            sx: {
                              fontSize: '0.875rem',
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => setShowNewPassword(!showNewPassword)}
                                  edge="end"
                                  size="small"
                                  sx={{ color: 'text.secondary' }}
                                >
                                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 0.75,
                            fontWeight: 500,
                            color: 'text.primary',
                            fontSize: '0.875rem',
                          }}
                        >
                          Confirm Password
                        </Typography>
                        <TextField
                          required
                          fullWidth
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={resetPasswordMutation.isPending || !token}
                          placeholder="Confirm new password"
                          InputProps={{
                            sx: {
                              fontSize: '0.875rem',
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  edge="end"
                                  size="small"
                                  sx={{ color: 'text.secondary' }}
                                >
                                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={resetPasswordMutation.isPending || !newPassword || !confirmPassword || !token}
                        sx={{
                          py: 1.5,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                        }}
                      >
                        {resetPasswordMutation.isPending ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          'Reset Password'
                        )}
                      </Button>
                    </Box>

                    {/* Back to Login */}
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Button
                        onClick={() => navigate('/login')}
                        variant="text"
                        sx={{ fontSize: '0.875rem', textTransform: 'none' }}
                      >
                        Back to Login
                      </Button>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
    </PageTransition>
  );
};

export default ResetPassword;
